import React from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import { CheckCircle } from "@mui/icons-material";
import GaugeChartMUI from "./charts/GaugeChartMUI";
import TrendChartMUI from "./charts/TrendChartMUI";
import CircularProgressMUI from "./charts/CircularProgressMUI";
import ProgressBarMUI from "./charts/ProgressBarMUI";
import DonutChartMUI from "./charts/DonutChartMUI";
import transformOWASPData from "./charts/transformOWASPData";
import InfoIcon from "@mui/icons-material/Info";

// Enhanced Donut Chart with center text
const DonutChart = ({ data, title, centerText, tooltipKey, tooltipTexts }) => {
  const COLORS = ["#f44336", "#ff5722", "#ff9800", "#4caf50"];

  const renderCustomizedLabel = ({ cx, cy }) => {
    return (
      <text
        x={cx}
        y={cy}
        fill="#333"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="24"
        fontWeight="bold"
      >
        {centerText}
      </text>
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "#5391F7",
        borderRadius: 2,
        position: "relative", // Add for absolute positioning
      }}
    >
      {/* Info Icon - Top Right Corner */}
      {tooltipKey && tooltipTexts && (
        <Tooltip
          title={tooltipTexts[tooltipKey]}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 300,
                fontSize: "14px",
                lineHeight: 1.4,
              },
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              padding: "4px",
              color: "#5391F7",
              "&:hover": {
                backgroundColor: "rgba(83, 145, 247, 0.1)",
              },
            }}
            aria-label="Info"
          >
            <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>
      )}
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          align="center"
          sx={{ paddingRight: 4, paddingLeft: 4 }}
        >
          {title}
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={centerText ? renderCustomizedLabel : false}
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            {/* <Tooltip /> */}
          </PieChart>
        </ResponsiveContainer>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          flexWrap="wrap"
          sx={{ mt: 2 }}
        >
          {data.map((entry, index) => (
            <Chip
              key={entry.name}
              label={`${entry.name}: ${entry.value}`}
              size="small"
              sx={{
                bgcolor: COLORS[index % COLORS.length],
                color: "white",
                mb: 0.5,
              }}
            />
          ))}
        </Stack>
      </CardContent>
    </Box>
  );
};

// Progress Bar Chart Component
const ProgressBarChart = ({ data, title, tooltipKey, tooltipTexts }) => {
  const maxValue = Math.max(...data.map((d) => d.value));

  return (
    <Box
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "#5391F7",
        borderRadius: 2,
        position: "relative", // Add for absolute positioning
      }}
    >
      {/* Info Icon - Top Right Corner */}
      {tooltipKey && tooltipTexts && (
        <Tooltip
          title={tooltipTexts[tooltipKey]}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 300,
                fontSize: "14px",
                lineHeight: 1.4,
              },
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              padding: "4px",
              color: "#5391F7",
              "&:hover": {
                backgroundColor: "rgba(83, 145, 247, 0.1)",
              },
            }}
            aria-label="Info"
          >
            <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>
      )}

      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ paddingRight: 3, paddingLeft: 3 }} // Prevent overlap with icon
        >
          {title}
        </Typography>
        <Stack spacing={2}>
          {data.map((item, index) => (
            <Box key={index}>
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.value}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={(item.value / maxValue) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: item.color || "primary.main",
                  },
                }}
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Box>
  );
};
// Transform closedVulnerabilitiesBySeverity to ProgressBarChart data format
const transformClosedVulnToChartData = (closedVulnerabilitiesBySeverity) => {
  const severityColors = {
    Critical: "#f44336", // Red
    High: "#ff5722", // Deep Orange
    Medium: "#ff9800", // Orange
    Low: "#4caf50", // Green
  };

  return Object.entries(closedVulnerabilitiesBySeverity)
    .filter(([severity, count]) => count > 0) // Only show severities with vulnerabilities
    .map(([severity, count]) => ({
      name: severity,
      value: count,
      color: severityColors[severity],
    }));
};

// Comparison Bar Chart
const ComparisonBarChart = ({ data, title }) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            {/* <Tooltip /> */}
            <Legend />
            <Bar dataKey="run1" fill="#f44336" name="Run 1" />
            <Bar dataKey="run2" fill="#4caf50" name="Run 2" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Compliance Requirements
const ComplianceCard = ({ title, status }) => {
  const statusColors = {
    Meets: { bg: "#bbf7d0", text: "#1e40af", border: "#bfdbfe" },
    Partial: { bg: "#fefce8", text: "#a16207", border: "#fde047" },
    Failed: { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
  };

  return (
    <Box textAlign="center" width={100}>
      <Typography
        variant="caption"
        fontWeight="medium"
        display="block"
        mb={0.5}
      >
        {title}
      </Typography>
      <Chip
        icon={<CheckCircle sx={{ fontSize: "12px !important" }} />}
        label={status}
        size="small"
        sx={{
          bgcolor: statusColors[status].bg,
          color: statusColors[status].text,
          border: `1px solid ${statusColors[status].border}`,
          fontSize: "0.75rem",
          height: 24,
          "& .MuiChip-icon": {
            fontSize: "12px",
          },
        }}
      />
    </Box>
  );
};

const ComplianceCard1 = ({ title, status }) => {
  const statusColors = {
    Meets: { bg: "#f0f9ff", text: "#1e40af", border: "#bfdbfe" },
    Partial: { bg: "#fefce8", text: "#a16207", border: "#fde047" },
    "Doesn't Meet": { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
  };

  return (
    <Box textAlign="center">
      <Typography
        variant="caption"
        fontWeight="medium"
        display="block"
        mb={0.5}
      >
        {title}
      </Typography>
      <Chip
        icon={<CheckCircle sx={{ fontSize: "12px !important" }} />}
        label={status}
        size="small"
        sx={{
          bgcolor: statusColors[status].bg,
          color: statusColors[status].text,
          border: `1px solid ${statusColors[status].border}`,
          fontSize: "0.75rem",
          height: 24,
          "& .MuiChip-icon": {
            fontSize: "12px",
          },
        }}
      />
    </Box>
  );
};

// Transform severityBreakdown to DonutChart data format
const transformSeverityToChartData = (severityBreakdown) => {
  return Object.entries(severityBreakdown).map(([severity, count]) => ({
    name: severity,
    value: count,
  }));
};

// Calculate total vulnerabilities for center text
const calculateTotalVulnerabilities = (severityBreakdown) => {
  return Object.values(severityBreakdown).reduce(
    (total, count) => total + count,
    0
  );
};

const OwaspVulnerabilityChart = ({ data, title, vulnerabilities = [] }) => {
  const maxValue = Math.max(...data.map((d) => d.value));

  // Function to get vulnerabilities for a specific category
  const getVulnerabilitiesForCategory = (categoryName) => {
    return vulnerabilities.filter(
      (vuln) => vuln.owasp_category === categoryName
    );
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Stack spacing={3}>
          {data.map((item, index) => {
            const categoryVulns = getVulnerabilitiesForCategory(item.name);

            return (
              <Box key={index}>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    {item.name}
                  </Typography>
                  <Chip
                    label={item.value}
                    size="small"
                    sx={{
                      bgcolor: item.color,
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "40px",
                    }}
                  />
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={(item.value / maxValue) * 100}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: "grey.200",
                    mb: 1,
                    "& .MuiLinearProgress-bar": {
                      bgcolor: item.color,
                      borderRadius: 5,
                    },
                  }}
                />

                {/* Show individual vulnerabilities */}
                {categoryVulns.length > 0 && (
                  <Box sx={{ ml: 1, mb: 1 }}>
                    {categoryVulns.map((vuln, vulnIndex) => (
                      <Typography
                        key={vulnIndex}
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary",
                          fontSize: "0.7rem",
                          lineHeight: 1.2,
                          mb: 0.3,
                        }}
                      >
                        • {vuln["vulnerability name"]}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>

        {/* Summary */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            Total Vulnerabilities:{" "}
            {data.reduce((sum, item) => sum + item.value, 0)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Transform the OWASP metrics data
const transformOwaspData = (owaspMetrics) => {
  const categoryCount = {};

  // Count vulnerabilities by OWASP category
  owaspMetrics.forEach((item) => {
    const category = item.owasp_category;
    categoryCount[category] = (categoryCount[category] || 0) + 1;
  });

  // Convert to array format with colors
  const colorMap = {
    "A01:2021 - Broken Access Control": "#FF6B6B",
    "A03:2021 - Injection": "#4ECDC4",
    "A05:2021 - Security Misconfiguration": "#45B7D1",
    "A08:2021 - Software and Data Integrity Failures": "#96CEB4",
    "A02:2021 - Cryptographic Failures": "#FFEAA7",
    "A04:2021 - Insecure Design": "#DDA0DD",
    "A06:2021 - Vulnerable and Outdated Components": "#98D8C8",
    "A07:2021 - Identification and Authentication Failures": "#F7DC6F",
    "A09:2021 - Security Logging and Monitoring Failures": "#BB8FCE",
    "A10:2021 - Server-Side Request Forgery": "#85C1E9",
  };

  return Object.entries(categoryCount).map(([category, count]) => ({
    name: category,
    value: count,
    color: colorMap[category] || "#74B9FF",
  }));
};

// Transform function
const transformSeverityData = (severityData) => {
  const colorMap = {
    Critical: "#f44336",
    High: "#ff5722",
    Medium: "#ff9800",
    Low: "#4caf50",
  };

  return Object.entries(severityData).map(([severity, count]) => ({
    name: severity,
    value: count,
    color: colorMap[severity],
  }));
};

const tooltipTexts = {
  "Run1 Security Posture":
    "Represents the percentage of the overall security posture in the application for Run 2. A lower value indicates better security hygiene and stronger defences.",
  "Run1 Business Impact":
    "Indicates how vulnerabilities can disrupt business operations or services. Lower percentages suggest minimal risk to business continuity.",
  "Run1 Technical Impact":
    "Reflects the level of technical damage vulnerabilities can cause (data loss, system downtime). A lower value signifies a more resilient system.",
  "Run1 Vulnerability Count":
    "Total number of verified (true positive) vulnerabilities found. Fewer vulnerabilities show better application security quality.",
  "Run1 Return on Investment":
    "Indicates how cost-effective your security testing is. A lower ROI means more value achieved for the cost spent.",
  "Run1 Risk Rating":
    "Represents how severe the identified vulnerabilities are, based on their criticality. A lower risk rating means fewer high-impact issues, indicating stronger security.",
  "Run1 Risk Rating Weighted Score":
    "A cumulative weighted score considering severity and count. Lower scores reflect reduced overall application risk.",
  "Run1 OWASP Top 10 Standards":
    "Indicates whether critical vulnerabilities align with OWASP Top 10. Helps assess coverage against industry-recognized threats.",
  "Run1 True Positive Rate":
    "Percentage of actual, real vulnerabilities correctly identified. A high TP rate indicates strong detection capabilities and minimizes the risk of undetected vulnerabilities.",
  "Run1 False Positive Rate":
    "Percentage of flagged issues that turned out to be incorrect. A high FP rate leads to unnecessary investigation effort, alert fatigue, and reduced trust in the security tool.",
  "Run1 Vulnerability Distribution by Severity":
    "Breakdown of vulnerabilities across Critical, High, Medium, and Low. Helps prioritize fixes based on severity.",
  "Run1 Vulnerability Distribution by OWASP Top 10":
    "Categorizes issues based on OWASP Top 10 risk types (e.g., Injection, Broken Auth). Helps understand common threat types in the app.",
  "Run2 Security Posture":
    "Represents the percentage of the overall security posture in the application for Run 2. A lower value indicates better security hygiene and stronger defences.",
  "Run2 Business Impact":
    "Indicates how vulnerabilities can disrupt business operations or services. Lower percentages suggest minimal risk to business continuity.",
  "Run2 Technical Impact":
    "Reflects the level of technical damage vulnerabilities can cause (data loss, system downtime). A lower value signifies a more resilient system.",
  "Run2 VulnerabilityCount":
    "Total number of verified (true positive) vulnerabilities found. Fewer vulnerabilities show better application security quality.",
  "Run2 ROI":
    "Indicates how cost-effective your security testing is. A lower ROI means more value achieved for the cost spent.",
  "Run2 RiskRating":
    "Represents how severe the identified vulnerabilities are, based on their criticality. A lower risk rating means fewer high-impact issues, indicating stronger security.",
  "Run2 RiskRating WeightedScore":
    "A cumulative weighted score considering severity and count. Lower scores reflect reduced overall application risk.",
  "Remediation Effectiveness":
    "Indicates how many previously identified vulnerabilities have been successfully fixed. Higher values reflect strong remediation efforts.",
  "Risk Exposure Reduction":
    "Measures how much the overall risk level has decreased since the last scan. Higher percentages show better risk containment.",
  "Attack Surface Reduction":
    "Represents how much the application’s potential points of entry have been reduced. A higher value indicates stronger hardening of the application.",
  "Average Age of Vulnerabilities":
    "Indicates how long vulnerabilities remain open. Older issues suggest delayed remediation, increasing potential risk.",
  "Average Time to Remediation":
    "Reflects the average time taken to resolve vulnerabilities. Lower times indicate faster and more efficient response cycles.",
  "Run2 OWASP Top 10":
    "Indicates whether all OWASP Top 10 vulnerability categories were identified and addressed. Useful for assessing security coverage.",
  "Open Vulnerabilities by Severity":
    "Categorizes current unresolved issues by severity. Helps identify risk hotspots that still need remediation.",
  "Closed Vulnerabilities by Severity":
    "Shows how many vulnerabilities have been fixed, grouped by severity. Helps measure progress and prioritization.",
  "New Vulnerabilities in Run 2":
    "Represents the vulnerabilities discovered for the first time in Run 2. Useful for spotting emerging risks or regressions.",
  "Run2 Vulnerability Distribution by OWASP Top 10":
    "Categorizes issues based on OWASP Top 10 risk types (e.g., Injection, Broken Auth). Helps understand common threat types in the app.",
};

const getSecurityPostureDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Posture";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Posture";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Posture";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Posture";
  } else {
    return "Unknown";
  }
};

// Function to get dynamic description based on business impact score
const getBusinessImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  } else {
    return "Unknown Impact";
  }
};

// Helper to turn a technical-impact score into a label
const getTechnicalImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  }
  return "Unknown Impact";
};

// Function to get dynamic description based on risk weighted score
const getRiskWeightedDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Risk";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Risk";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Risk";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Risk";
  } else {
    return "Unknown Risk";
  }
};

function SecurityMetrics({ securityMetrics }) {
  const run1 = securityMetrics.run1Metrics || {};
  const run2 = securityMetrics.run2Metrics || {};

  const owaspData = transformOWASPData(run1.owasp_metrics);

  const securityPosture = {
    title: "Security Posture",
    tooltipKey: "Run1 Security Posture",
    chart: (
      <GaugeChartMUI
        value={run1.securityPosture}
        max={10}
        color="#dc2626"
        size={150}
      />
    ),
    value: `${run1.securityPosture}/10`,
    description: getSecurityPostureDescription(run1.securityPosture),
  };

  const businessImpact = {
    title: "Business Impact",
    tooltipKey: "Run1 Business Impact",
    chart: (
      <GaugeChartMUI
        value={run1.businessImpact}
        max={10}
        color="#ea580c"
        size={150}
      />
    ),
    value: `${run1.businessImpact}/10`,
    description: getBusinessImpactDescription(run1.businessImpact),
  };

  const technicalImpact = {
    title: "Technical Impact",
    tooltipKey: "Run1 Technical Impact",
    chart: (
      <GaugeChartMUI
        value={run1.technicalImpact}
        max={10}
        color="#8b5cf6"
        size={150}
      />
    ),
    value: `${run1.technicalImpact}/10`,
    description: getTechnicalImpactDescription(run1.technicalImpact),
  };

  const vulnerabilityCount = {
    title: "Vulnerability Count",
    tooltipKey: "Run1 Vulnerability Count",
    chart: (
      <TrendChartMUI
        value={run1.vulnerabilitiesCount}
        trend="up"
        color="#dc2626"
        size={90}
      />
    ),
    value: run1.vulnerabilitiesCount,
    description: "True Positive Vulnerabilities",
  };

  const roiMetric = {
    title: "Return on Investment",
    tooltipKey: "Run1 Return on Investment",
    chart: (
      <Typography variant="h3" fontWeight="bold" color="success.main">
        {run1.ROI}%
      </Typography>
    ),
    description: "ROI Performance",
  };

  const riskNumeric = {
    title: "Risk Rating",
    tooltipKey: "Run1 Risk Rating",
    chart: (
      <GaugeChartMUI
        value={run1.riskRating.toFixed(1)}
        max={5.5}
        color="#dc2626"
        size={150}
      />
    ),
    description: getRiskWeightedDescription(
      run1.riskRatingWeightedScore.toFixed(1)
    ),
  };

  const riskWeighted = {
    title: "Risk Rating Weighted Score",
    tooltipKey: "Run1 Risk Rating Weighted Score",
    chart: (
      <GaugeChartMUI
        value={run1.riskRatingWeightedScore.toFixed(1)}
        max={10}
        color="#ea580c"
        size={150}
      />
    ),
    description: getRiskWeightedDescription(
      run1.riskRatingWeightedScore.toFixed(1)
    ),
  };

  const getComplianceStatus = () => {
    const { Critical = 0, High = 0, Medium = 0 } = run1.severityBreakdown;

    if (Critical >= 1 || High >= 1 || Medium >= 1) return "Doesn't Meet";
    return "Meets";
  };

  const status = getComplianceStatus();
  const description =
    status === "Meets"
      ? "All Standards Met"
      : status === "Partial"
      ? "Partially Meets Standards"
      : "Standards Not Met";

  const compliance = {
    title: "OWASP Top 10 Standards",
    tooltipKey: "Run1 OWASP Top 10 Standards",
    chart: (
      <Grid container spacing={1} mb={2}>
        <Grid item xs={12}>
          <ComplianceCard1 title="OWASP" status={status} />
        </Grid>
      </Grid>
    ),
    description: description,
  };

  const truePositiveRate = {
    title: "True Positive Rate",
    tooltipKey: "Run1 True Positive Rate",
    chart: (
      <CircularProgressMUI
        percentage={run1.truePositiveRate}
        color="#3b82f6"
        size={120}
      />
    ),
    value: `${run1.truePositiveRate.toFixed(1)}%`,
    description: "Accurate Vulnerability Detection",
  };

  const falsePositiveRate = {
    title: "False Positive Rate",
    tooltipKey: "Run1 False Positive Rate",
    chart: (
      <CircularProgressMUI
        percentage={run1.falsePositiveRate}
        color="#16a34a"
        size={120}
      />
    ),
    value: `${run1.falsePositiveRate.toFixed(1)}%`,
    description: "False Alarm Rate",
  };

  const run2SecurityPosture = {
    title: "Security Posture",
    tooltipKey: "Run2 Security Posture",
    chart: (
      <GaugeChartMUI
        value={run2.securityPosture.toFixed(1)}
        max={10}
        color="#dc2626"
        size={150}
      />
    ),
    value: `${run2.securityPosture.toFixed(1)}/10`,
    description: getSecurityPostureDescription(run2.securityPosture.toFixed(1)),
  };

  const run2BusinessImpact = {
    title: "Business Impact",
    tooltipKey: "Run2 Business Impact",
    chart: (
      <GaugeChartMUI
        value={run2.businessImpact.toFixed(1)}
        max={10}
        color="#ea580c"
        size={150}
      />
    ),
    value: `${run2.businessImpact.toFixed(1)}/10`,
    description: getBusinessImpactDescription(run2.businessImpact.toFixed(1)),
  };

  const run2TechnicalImpact = {
    title: "Technical Impact",
    tooltipKey: "Run2 Technical Impact",
    chart: (
      <GaugeChartMUI
        value={run2.technicalImpact.toFixed(1)}
        max={10}
        color="#8b5cf6"
        size={150}
      />
    ),
    value: `${run2.technicalImpact.toFixed(1)}/10`,
    description: getTechnicalImpactDescription(run2.technicalImpact.toFixed(1)),
  };

  const run2VulnerabilityCount = {
    title: "Vulnerability Count",
    tooltipKey: "Run2 VulnerabilityCount",
    chart: (
      <TrendChartMUI
        value={run2.notFixedVulnerabilities}
        trend="up"
        color="#dc2626"
        size={90}
      />
    ),
    value: run2.notFixedVulnerabilities,
    description: "Not fixed from Run1",
  };

  const run2ROIMetric = {
    title: "Return on Investment",
    tooltipKey: "Run2 ROI",
    chart: (
      <Typography variant="h3" fontWeight="bold" color="success.main">
        {run2.roiPercentage}%
      </Typography>
    ),
    description: "ROI Performance",
  };

  const run2RiskNumeric = {
    title: "Risk Rating",
    tooltipKey: "Run2 RiskRating",
    chart: (
      <GaugeChartMUI
        value={run2.riskRating.toFixed(1)}
        max={5.5}
        color="#dc2626"
        size={150}
      />
    ),
    description: getRiskWeightedDescription(
      run2.riskRatingWeightedScore.toFixed(1)
    ),
  };

  const run2RiskWeighted = {
    title: "Risk Rating Weighted Score",
    tooltipKey: "Run2 RiskRating WeightedScore",
    chart: (
      <GaugeChartMUI
        value={run2.riskRatingWeightedScore.toFixed(1)}
        max={10}
        color="#ea580c"
        size={150}
      />
    ),
    description: getRiskWeightedDescription(
      run2.riskRatingWeightedScore.toFixed(1)
    ),
  };

  const getrun2ComplianceStatus = () => {
    const {
      Critical = 0,
      High = 0,
      Medium = 0,
    } = run2.notFixedSeverityDistribution;

    if (Critical >= 1 || High >= 1 || Medium >= 1) return "Doesn't Meet";
    return "Meets";
  };

  const run2Status = getrun2ComplianceStatus();
  const run2Description =
    run2Status === "Meets"
      ? "All Standards Met"
      : run2Status === "Partial"
      ? "Partially Meets Standards"
      : "Standards Not Met";

  const run2Compliance = {
    title: "OWASP Top 10 Standards",
    tooltipKey: "Run2 OWASP Top 10",
    chart: (
      <Grid container spacing={1} mb={2}>
        <Grid item xs={12}>
          <ComplianceCard1 title="OWASP" status={run2Status} />
        </Grid>
      </Grid>
    ),
    description: run2Description,
  };

  const run2Remediation = {
    title: "Remediation Effectiveness",
    tooltipKey: "Remediation Effectiveness",
    chart: (
      <GaugeChartMUI
        value={run2.remediationEffectiveness.toFixed(1)}
        max={100}
        color="#ea580c"
        size={180}
      />
    ),
    description: "Remediation Rate",
  };

  const run2RiskExposure = {
    title: "Risk Exposure Reduction",
    tooltipKey: "Risk Exposure Reduction",
    chart: (
      <GaugeChartMUI
        value={run2.riskExposureReduction.toFixed(1)}
        max={100}
        color="#ea580c"
        size={180}
      />
    ),
    description: "Risk Reduction Rate",
  };
  const run2AttackSurface = {
    title: "Attack Surface Reduction",
    tooltipKey: "Attack Surface Reduction",
    chart: (
      <GaugeChartMUI
        value={run2.attackSurfaceReduction.toFixed(1)}
        max={10}
        color="#ea580c"
        size={150}
      />
    ),
    description: "Surface Reduction Rate",
  };

  const run2AverageAgeofVulnerabilities = {
    title: "Average Age of Vulnerabilities",
    tooltipKey: "Average Age of Vulnerabilities",
    chart: (
      <TrendChartMUI
        value={run2.averageAgeOfVulnerability}
        trend="up"
        color="#dc2626"
        size={90}
      />
    ),
    value: `${run2.averageAgeOfVulnerability} Days`,
    description: "Exposure Time",
  };

  const run2AverageTimeRemediation = {
    title: "Average time to Remediation",
    tooltipKey: "Average Time to Remediation",
    chart: (
      <TrendChartMUI
        value={run2.averageTimeToRemediation}
        trend="up"
        color="#dc2626"
        size={90}
      />
    ),
    value: `${run2.averageTimeToRemediation} Days`,
    description: "Average Fix Time",
  };

  // Transform severityBreakdown data
  const run1VulnData = transformSeverityToChartData(run1.severityBreakdown);
  const totalVulnerabilities = calculateTotalVulnerabilities(
    run1.severityBreakdown
  );

  const run2VulnData = transformSeverityToChartData(
    run2.notFixedSeverityDistribution
  );
  const run2totalVulnerabilities = calculateTotalVulnerabilities(
    run2.notFixedSeverityDistribution
  );

  const run2OpenVulnData = transformSeverityToChartData(
    run2.openVulnerabilitiesBySeverity
  );
  const run2OpentotalVulnerabilities = calculateTotalVulnerabilities(
    run2.openVulnerabilitiesBySeverity
  );

  // Transform closedVulnerabilitiesBySeverity data
  const closedVulnData = transformClosedVulnToChartData(
    run2.closedVulnerabilitiesBySeverity
  );

  const run1OwaspTransformed = transformOwaspData(run1.owasp_metrics);

  // Transformed data
  const run2NewVulnData = transformSeverityData(
    run2.newVulnerabilitiesBySeverity
  );

  const run2OwaspDataTransformed = transformOwaspData(
    run2.owaspNotFixedVulnerabilities
  );

  const compliance2 = {
    title: "Run-2 Compliance Requirements",
    chart: (
      <Grid container spacing={1} mb={2}>
        <Grid item xs={3}>
          <ComplianceCard title="PCI DSS" status="Meets" />
        </Grid>
        <Grid item xs={3}>
          <ComplianceCard title="HIPAA" status="Meets" />
        </Grid>
        <Grid item xs={3}>
          <ComplianceCard title="GDPR" status="Meets" />
        </Grid>
        <Grid item xs={3}>
          <ComplianceCard title="ISO 27001" status="Meets" />
        </Grid>
      </Grid>
    ),
    description: "All Standards Met",
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
      }}
    >
      {/* Run 1 - Baseline Security Metrics */}
      <Box sx={{ p: { xs: 2 }, mb: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
            Run 1 - Baseline Security Metrics
          </Typography>
          {/* <Typography variant="body2" color="text.secondary">
            Jan 15, 2024
          </Typography> */}
        </Stack>
        {/* Security Posture, Business Impact, Technical Impact, Vulnerability Count */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mb: { xs: 4, md: 6 } }}
        >
          {[
            securityPosture,
            businessImpact,
            technicalImpact,
            vulnerabilityCount,
          ].map((metric, i) => (
            <Grid key={i} item xs={12} sm={6} md={6} lg={3}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "#5391F7",
                  borderRadius: 2,
                  pt: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative", // Added for absolute positioning
                }}
              >
                {/* Info Icon - Top Right Corner */}
                <Tooltip
                  title={tooltipTexts[metric.tooltipKey]}
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 300,
                        fontSize: "14px",
                        lineHeight: 1.4,
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      padding: "4px",
                      color: "#5391F7",
                      "&:hover": {
                        backgroundColor: "rgba(83, 145, 247, 0.1)",
                      },
                    }}
                    aria-label="Info"
                  >
                    <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                  </IconButton>
                </Tooltip>

                <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
                  <Typography
                    textAlign="center"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 500,
                      paddingRight: 3, // Prevents title from overlapping with icon
                    }}
                  >
                    {metric.title}
                  </Typography>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    {/* Chart and Value Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "center",
                      }}
                    >
                      {metric.title !== "Vulnerability Count" && (
                        <Box>{metric.chart}</Box>
                      )}
                      <Typography sx={{ fontSize: "29px", fontWeight: 600 }}>
                        {metric.title === "Vulnerability Count" && metric.value}
                      </Typography>
                    </Box>

                    {/* Description Section - Fixed at bottom */}
                    <Box
                      sx={{
                        textAlign: "center",
                        minHeight: "60px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 400,
                        }}
                      >
                        {metric.description}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Return on Investment (ROI), Risk Rating (Numeric, Risk Rating Weighted Score) & OWASP Top 10 Standards */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 3, md: 4 } }}
        >
          {[roiMetric, riskNumeric, riskWeighted, compliance].map(
            (metric, i) => (
              <Grid key={i} item xs={12} sm={6} md={6} lg={3}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "#5391F7",
                    borderRadius: 2,
                    pt: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {/* Info Icon - Top Right Corner */}
                  <Tooltip
                    title={tooltipTexts[metric.tooltipKey]}
                    placement="top"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 300,
                          fontSize: "14px",
                          lineHeight: 1.4,
                        },
                      },
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        padding: "4px",
                        color: "#5391F7",
                        "&:hover": {
                          backgroundColor: "rgba(83, 145, 247, 0.1)",
                        },
                      }}
                      aria-label="Info"
                    >
                      <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                    </IconButton>
                  </Tooltip>
                  <Stack
                    spacing={2}
                    alignItems="center"
                    sx={{ height: "100%" }}
                  >
                    <Typography
                      textAlign="center"
                      sx={{
                        fontSize: "18px",
                        fontWeight: 500,
                        paddingRight: 6,
                        paddingLeft: 6,
                      }}
                    >
                      {metric.title}
                    </Typography>

                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between", // Changed from center
                        width: "100%",
                      }}
                    >
                      {/* Chart Section */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          flex: 1,
                          justifyContent: "center",
                        }}
                      >
                        <Box>{metric.chart}</Box>
                      </Box>

                      {/* Description Section - Fixed at bottom */}
                      <Box
                        sx={{
                          textAlign: "center",
                          minHeight: "60px", // Fixed minimum height for descriptions
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 400,
                            lineHeight: 1.4,
                          }}
                        >
                          {metric.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            )
          )}
        </Grid>

        {/* Run1 True & False Positive Rates */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 4, md: 6 } }}
        >
          {[truePositiveRate, falsePositiveRate].map((metric, i) => (
            <Grid key={i} item xs={12} md={6}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "#5391F7",
                  borderRadius: 2,
                  p: 2,
                  height: "100%",
                  position: "relative",
                }}
              >
                <Tooltip
                  title={tooltipTexts[metric.tooltipKey]}
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 300,
                        fontSize: "14px",
                        lineHeight: 1.4,
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      padding: "4px",
                      color: "#5391F7",
                      "&:hover": {
                        backgroundColor: "rgba(83, 145, 247, 0.1)",
                      },
                    }}
                    aria-label="Info"
                  >
                    <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                  </IconButton>
                </Tooltip>
                <Stack spacing={2} alignItems="center" height="100%">
                  <Typography
                    textAlign="center"
                    sx={{ fontSize: "21px", fontWeight: 500 }}
                  >
                    {metric.title}
                  </Typography>
                  <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box mb={3}>{metric.chart}</Box>
                    <Box textAlign="center">
                      {/* <Typography variant="h5" fontWeight="bold">
                              {metric.value}
                            </Typography> */}
                      <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                        {metric.description}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Grid
          container
          spacing={{ xs: 2, sm: 2 }}
          sx={{
            mt: { xs: 4, md: 6 },
            mb: { xs: 4, md: 6 },
          }}
        >
          {/* Vulnerability Distribution */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "#5391F7",
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Tooltip
                title={
                  tooltipTexts["Run1 Vulnerability Distribution by Severity"]
                }
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
              <Typography sx={{ fontSize: "23px", fontWeight: 400 }}>
                Vulnerability Distribution by Severity
              </Typography>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mt={2}
                flex={1}
              >
                <DonutChartMUI singleRunSecurityMetrics={run1} />
              </Box>
            </Box>
          </Grid>

          {/* OWASP Top 10 Distribution */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "#5391F7",
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                height: "100%", // Ensure consistent height
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Tooltip
                title={
                  tooltipTexts[
                    "Run1 Vulnerability Distribution by OWASP Top 10"
                  ]
                }
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
              <Typography sx={{ fontSize: "23px", fontWeight: 400, mb: 2 }}>
                Vulnerability Distribution by OWASP Top 10
              </Typography>

              {owaspData.length > 0 ? (
                <Stack spacing={2} width="100%" flex={1}>
                  {owaspData.map((item, i) => (
                    <ProgressBarMUI
                      key={i}
                      label={item.label}
                      value={item.value}
                      total={totalVulnerabilities}
                      color={item.color}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  width="100%"
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  No OWASP categorization data available
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Run 2 - Current Security Metrics */}
      <Box sx={{ p: 3, mb: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6">Run 2 - Current Security Metrics</Typography>
          {/* <Typography variant="body2" color="text.secondary">
            Jan 29, 2024
          </Typography> */}
        </Stack>

        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mb: { xs: 4, md: 4 } }}
        >
          {[
            run2SecurityPosture,
            run2BusinessImpact,
            run2TechnicalImpact,
            run2VulnerabilityCount,
          ].map((metric, i) => (
            <Grid key={i} item xs={12} sm={6} md={6} lg={3}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "#5391F7",
                  borderRadius: 2,

                  pt: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <Tooltip
                  title={tooltipTexts[metric.tooltipKey]}
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 300,
                        fontSize: "14px",
                        lineHeight: 1.4,
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      padding: "4px",
                      color: "#5391F7",
                      "&:hover": {
                        backgroundColor: "rgba(83, 145, 247, 0.1)",
                      },
                    }}
                    aria-label="Info"
                  >
                    <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                  </IconButton>
                </Tooltip>
                <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
                  <Typography
                    textAlign="center"
                    sx={{ fontSize: "18px", fontWeight: 500 }}
                  >
                    {metric.title}
                  </Typography>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between", // Changed from center
                      width: "100%",
                    }}
                  >
                    {/* Chart and Value Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "center",
                      }}
                    >
                      {metric.title !== "Vulnerability Count" && (
                        <Box>{metric.chart}</Box>
                      )}
                      <Typography
                        sx={{
                          fontSize: "29px",
                          fontWeight: 600,
                          paddingRight: 6,
                          paddingLeft: 6,
                        }}
                      >
                        {metric.title === "Vulnerability Count" && metric.value}
                      </Typography>
                    </Box>

                    {/* Description Section - Fixed at bottom */}
                    <Box
                      sx={{
                        textAlign: "center",
                        minHeight: "60px", // Fixed minimum height for descriptions
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 400,
                          // lineHeight: 1.4,
                        }}
                      >
                        {metric.description}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Return on Investment (ROI), Risk Rating (Numeric, Risk Rating Weighted Score) & OWASP Top 10 Standards */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mt: { xs: 4 }, mb: { xs: 3, md: 4 } }}
        >
          {[
            run2ROIMetric,
            run2RiskNumeric,
            run2RiskWeighted,
            run2Remediation,
          ].map((metric, i) => (
            <Grid key={i} item xs={12} sm={6} md={6} lg={3}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "#5391F7",
                  borderRadius: 2,
                  pt: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <Tooltip
                  title={tooltipTexts[metric.tooltipKey]}
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 300,
                        fontSize: "14px",
                        lineHeight: 1.4,
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      padding: "4px",
                      color: "#5391F7",
                      "&:hover": {
                        backgroundColor: "rgba(83, 145, 247, 0.1)",
                      },
                    }}
                    aria-label="Info"
                  >
                    <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                  </IconButton>
                </Tooltip>
                <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
                  <Typography
                    textAlign="center"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 500,
                      paddingRight: 6,
                      paddingLeft: 6,
                    }}
                  >
                    {metric.title}
                  </Typography>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between", // Changed from center
                      width: "100%",
                    }}
                  >
                    {/* Chart Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "center",
                      }}
                    >
                      <Box>{metric.chart}</Box>
                    </Box>

                    {/* Description Section - Fixed at bottom */}
                    <Box
                      sx={{
                        textAlign: "center",
                        minHeight: "60px", // Fixed minimum height for descriptions
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 400,
                          lineHeight: 1.4,
                        }}
                      >
                        {metric.description}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mt: { xs: 4 }, mb: { xs: 4, md: 6 } }}
        >
          {[
            // run2Remediation,
            run2RiskExposure,
            run2AttackSurface,
            run2AverageAgeofVulnerabilities,
            run2AverageTimeRemediation,
          ].map((metric, i) => (
            <Grid key={i} item xs={12} sm={6} md={6} lg={3}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "#5391F7",
                  borderRadius: 2,
                  pt: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <Tooltip
                  title={tooltipTexts[metric.tooltipKey]}
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 300,
                        fontSize: "14px",
                        lineHeight: 1.4,
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      padding: "4px",
                      color: "#5391F7",
                      "&:hover": {
                        backgroundColor: "rgba(83, 145, 247, 0.1)",
                      },
                    }}
                    aria-label="Info"
                  >
                    <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                  </IconButton>
                </Tooltip>
                <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
                  <Typography
                    textAlign="center"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 500,
                      paddingRight: 6,
                      paddingLeft: 6,
                    }}
                  >
                    {metric.title}
                  </Typography>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between", // Changed from center
                      width: "100%",
                    }}
                  >
                    {/* Chart and Value Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "center",
                      }}
                    >
                      {metric.title !== "Average Age of Vulnerabilities" &&
                        metric.title !== "Average time to Remediation" && (
                          <Box>{metric.chart}</Box>
                        )}
                      <Typography sx={{ fontSize: "29px", fontWeight: 600 }}>
                        {(metric.title === "Average Age of Vulnerabilities" ||
                          metric.title === "Average time to Remediation") &&
                          metric.value}
                      </Typography>
                    </Box>

                    {/* Description Section - Fixed at bottom */}
                    <Box
                      sx={{
                        textAlign: "center",
                        minHeight: "60px", // Fixed minimum height for descriptions
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 400,
                          // lineHeight: 1.4,
                        }}
                      >
                        {metric.description}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mt: { xs: 4 }, mb: { xs: 3, md: 4 } }}
        >
          {[run2Compliance].map((metric, i) => (
            <Grid key={i} item xs={12} sm={6} md={6} lg={3}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "#5391F7",
                  borderRadius: 2,
                  pt: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <Tooltip
                  title={tooltipTexts[metric.tooltipKey]}
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 300,
                        fontSize: "14px",
                        lineHeight: 1.4,
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      padding: "4px",
                      color: "#5391F7",
                      "&:hover": {
                        backgroundColor: "rgba(83, 145, 247, 0.1)",
                      },
                    }}
                    aria-label="Info"
                  >
                    <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                  </IconButton>
                </Tooltip>
                <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
                  <Typography
                    textAlign="center"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 500,
                      paddingRight: 6,
                      paddingLeft: 6,
                    }}
                  >
                    {metric.title}
                  </Typography>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between", // Changed from center
                      width: "100%",
                    }}
                  >
                    {/* Chart Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "center",
                      }}
                    >
                      <Box>{metric.chart}</Box>
                    </Box>

                    {/* Description Section - Fixed at bottom */}
                    <Box
                      sx={{
                        textAlign: "center",
                        minHeight: "60px", // Fixed minimum height for descriptions
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 400,
                          lineHeight: 1.4,
                        }}
                      >
                        {metric.description}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Run 2 Vulnerability Distribution by Severity */}
        {/* <Grid item xs={12} md={6}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "#5391F7",
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography sx={{ fontSize: "23px", fontWeight: 400 }}>
              Vulnerability Distribution by Severity
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={2}
              flex={1}
            >
              <DonutChartMUI singleRunSecurityMetrics={run2} />
            </Box>
          </Box>
        </Grid> */}

        {/* Run 2 Charts */}
        <Grid container spacing={3}>
          {/* <Grid item xs={12} md={3}>
            <DonutChart
              data={run2VulnData}
              title="Run 2 - Vulnerability Distribution by Severity"
              centerText={run2totalVulnerabilities.toString()}
            />
          </Grid> */}
          <Grid item xs={12} md={4}>
            <DonutChart
              data={run2OpenVulnData}
              title="Run 2 - Open Vulnerabilities by Severity"
              centerText={run2OpentotalVulnerabilities.toString()}
              tooltipKey="Open Vulnerabilities by Severity"
              tooltipTexts={tooltipTexts}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ProgressBarChart
              data={closedVulnData}
              title="Run 2 - Closed Vulnerabilities by Severity"
              tooltipKey="Closed Vulnerabilities by Severity"
              tooltipTexts={tooltipTexts}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                height: "100%",
                border: "1px solid #5391F7",
                borderRadius: 2,
                position: "relative",
              }}
            >
              <Tooltip
                title={tooltipTexts["New Vulnerabilities in Run 2"]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ paddingRight: 2, paddingLeft: 2 }}
                >
                  New Vulnerabilities in Run 2
                </Typography>
                <Stack spacing={2}>
                  {run2NewVulnData.map((item, index) => (
                    <Box key={index}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2">{item.name}</Typography>
                        <Chip
                          label={item.value}
                          size="small"
                          sx={{
                            bgcolor: item.color,
                            color: "white",
                            fontWeight: "bold",
                          }}
                        />
                      </Stack>
                    </Box>
                  ))}
                  <Divider />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      Total
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {run2NewVulnData.reduce(
                        (sum, item) => sum + item.value,
                        0
                      )}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={6} md={6} sx={{ mt: 4 }}>
          <Grid item xs={12} md={3}>
            <ProgressBarChart
              data={run2OwaspDataTransformed}
              title="Run 2 - Vulnerability Distribution by OWASP Top 10"
              tooltipKey="Run2 Vulnerability Distribution by OWASP Top 10"
              tooltipTexts={tooltipTexts}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default SecurityMetrics;
